import { useEffect } from 'react';

import { useGatewayRulesFormContext } from 'pages/GatewayRules/components/GatewayRulesForm/context/GatewayRulesFormContext';
import { GatewayRulesFormRemoveButton } from 'pages/GatewayRules/components/GatewayRulesForm/GatewayRulesFormRemoveButton';
import { useExpressionParamWithData } from 'pages/GatewayRules/hooks';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useFormContext } from 'react-hook-form';
import { ExpressionParam, ExpressionParamFieldType } from 'types/embedded/gatewayRule';

import { StyledExpressionCard } from './GatewayRulesFormRuleExpression.styled';
import { GatewayRulesFormRuleExpressionArrayInput } from './GatewayRulesFormRuleExpressionArrayInput';
import { GatewayRulesFormRuleExpressionInput } from './GatewayRulesFormRuleExpressionInput';
import { GatewayRulesFormRuleExpressionMultipleInput } from './GatewayRulesFormRuleExpressionMultipleInput';
import { GatewayRulesFormRuleExpressionOperator } from './GatewayRulesFormRuleExpressionOperator';
import { GatewayRulesFormRuleExpressionParams } from './GatewayRulesFormRuleExpressionParams';
import { GatewayRulesFormSkeletonExpression } from '../../GatewayRulesFormSkeleton/GatewayRulesFormSkeletonExpression';

type Props = {
  comparisonOperatorName: string;
  expressionIndex: number;
  expressionParamName: string;
  onRemoveExpressionClick: (index: number) => void;
  valueName: string;
};

export function GatewayRulesFormRuleExpression({
  comparisonOperatorName,
  expressionIndex,
  expressionParamName,
  onRemoveExpressionClick,
  valueName,
}: Props): JSX.Element {
  const translate = useTypedTranslate();
  const { watch, setValue, getValues } = useFormContext();
  const currentExpressionParam: ExpressionParam = watch(expressionParamName);

  const { expressionParams } = useGatewayRulesFormContext();
  const { expressionParamWithData, loadingExpressionParamWithData } = useExpressionParamWithData(
    currentExpressionParam?.alias,
  );

  const isCustomerIdMultiple = currentExpressionParam?.alias === 'customerId';

  const onChangeExpressionParam = () => {
    setValue(valueName, []);
    setValue(comparisonOperatorName, null);
  };

  useEffect(() => {
    if (!expressionParamWithData || loadingExpressionParamWithData) {
      return;
    }
    const currentOperator = getValues(comparisonOperatorName);

    if (!currentOperator) {
      setValue(comparisonOperatorName, expressionParamWithData?.comparisonOperators?.[0]);
    }
  }, [expressionParamName, expressionParamWithData, loadingExpressionParamWithData]);

  function getValueInput(): JSX.Element {
    if (isCustomerIdMultiple) {
      return <GatewayRulesFormRuleExpressionMultipleInput name={valueName} />;
    }

    if (currentExpressionParam?.fieldType === ExpressionParamFieldType.MultiSelect) {
      return (
        <GatewayRulesFormRuleExpressionArrayInput
          name={valueName}
          options={expressionParamWithData?.data || []}
        />
      );
    }

    return <GatewayRulesFormRuleExpressionInput name={valueName} />;
  }

  if (currentExpressionParam?.alias && loadingExpressionParamWithData) {
    return <GatewayRulesFormSkeletonExpression />;
  }

  return (
    <StyledExpressionCard>
      <GatewayRulesFormRuleExpressionParams
        expressionParams={expressionParams}
        name={expressionParamName}
        onChange={onChangeExpressionParam}
      />
      <GatewayRulesFormRuleExpressionOperator
        comparisonOperators={expressionParamWithData?.comparisonOperators || []}
        expressionParamName={currentExpressionParam?.name}
        isColored={isCustomerIdMultiple}
        name={comparisonOperatorName}
      />
      {getValueInput()}
      <GatewayRulesFormRemoveButton
        onClick={() => onRemoveExpressionClick(expressionIndex)}
        tooltipText={translate('app.gatewayRules.deleteExpression')}
      />
    </StyledExpressionCard>
  );
}
